import React, { useRef, useState, useEffect } from 'react';
import logoSrc from '../../../assets/images/simpleLogo.jpg';
import { validEmailRegex, validePassword } from '../../../utils/validators';
import Notification from '../../../components/Notification';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

export default function LoginForm({
    submitData,
    userAuthError,
    isLoading,
    setIsLoading,
    setUserAuthError,
    serverError,
    useTraditionalLogin,
    jumpcloudLogin,
    pageLoading,
}) {
    const [email, setEmail] = useState('');
    const passwordRef = useRef();

    const [emailError, setEmailError] = useState(false);
    const [passwordError, setPasswordError] = useState(false);

    // Notification state for server errors
    const [showSavedNotification, setShowSavedNotification] = useState(false);
    const [notifTitle, setNotifTitle] = useState('');
    const [notifDesc, setNotifDesc] = useState('');
    const [notifStatus, setNotifStatus] = useState('error');

    useEffect(() => {
        if (serverError) {
            setNotifTitle('There was an error');
            setNotifDesc('Please try logging in again. If you continue to encounter errors, please contact your manager.');
            setNotifStatus('error');
            setShowSavedNotification(true);
        }
    }, [serverError]);

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsLoading(true);
        const validEamil = validEmailRegex(email);
        const validPassword = validePassword(passwordRef.current.value);

        if (!validEamil) {
            setEmailError(true);
            setIsLoading(false);
            return;
        }

        if (!validPassword) {
            setPasswordError(true);
            setIsLoading(false);
            return;
        }
        setEmailError(false);
        submitData({
            email,
            password: passwordRef.current.value,
        });
    };

    const handleEmailFocusOut = () => {
        const validEamil = validEmailRegex(email);
        if (!validEamil) {
            setEmailError(true);
        } else {
            setEmailError(false);
        }
    };

    const handlePasswordFocusOut = () => {
        const validPassword = validePassword(passwordRef.current.value);

        if (!validPassword) {
            setPasswordError(true);
        } else {
            setPasswordError(false);
        }
    };

    return (
        <div className='flex flex-col flex-1 justify-center px-4 py-12 mb-36 sm:px-6 lg:flex-none lg:px-20 xl:px-24'>
            <div className='mx-auto w-full max-w-sm lg:w-96'>
                {pageLoading ? (
                    <div className='flex justify-center items-center w-full h-full'>
                        <div className='w-32 h-32 rounded-full border-t-2 border-b-2 border-gray-900 animate-spin'></div>
                    </div>
                ) : !useTraditionalLogin ? (
                    <div>
                        <div>
                            <img className='w-auto h-20' src={logoSrc} alt='Your Company' />
                            <h2 className='mt-6 text-3xl font-bold tracking-tight text-gray-900'>Employee Login</h2>
                        </div>

                        <div className='mt-8'>
                            <div className='mt-6'>
                                <form className='space-y-6'>
                                    <div>
                                        <label htmlFor='email' className='block text-sm font-medium text-gray-700'>
                                            Username
                                        </label>
                                        <div className='mt-1'>
                                            <input
                                                onBlur={handleEmailFocusOut}
                                                onFocus={() => setUserAuthError(false)}
                                                onChange={(e) => setEmail(e.target.value)}
                                                id='email'
                                                name='email'
                                                type='email'
                                                autoComplete='email'
                                                required
                                                className={classNames(
                                                    emailError ? 'border-2 border-red-400' : 'border border-gray-300',
                                                    'block px-3 py-2 w-full placeholder-gray-400 rounded-md border-gray-300 shadow-sm appearance-none focus:border-blue-primary focus:outline-none focus:ring-blue-primary sm:text-sm'
                                                )}
                                            />
                                        </div>
                                        {emailError && (
                                            <p className='pl-1 text-xs italic text-red-500'>Please use a valid email</p>
                                        )}
                                    </div>

                                    <div className='space-y-1'>
                                        <label htmlFor='password' className='block text-sm font-medium text-gray-700'>
                                            Password
                                        </label>
                                        <div className='mt-1'>
                                            <input
                                                ref={passwordRef}
                                                onFocus={() => setUserAuthError(false)}
                                                onBlur={handlePasswordFocusOut}
                                                id='password'
                                                name='password'
                                                type='password'
                                                autoComplete='current-password'
                                                required
                                                className={classNames(
                                                    passwordError ? 'border-2 border-red-400' : 'border border-gray-300',
                                                    'block px-3 py-2 w-full placeholder-gray-400 rounded-md border-gray-300 shadow-sm appearance-none focus:border-blue-primary focus:outline-none focus:ring-blue-primary sm:text-sm'
                                                )}
                                            />
                                        </div>
                                        {passwordError && (
                                            <p className='pl-1 text-xs italic text-red-500'>
                                                Password must be at least 5 characters
                                            </p>
                                        )}
                                    </div>

                                    {!isLoading ? (
                                        <button
                                            onClick={handleSubmit}
                                            type='submit'
                                            className='flex justify-center px-4 py-2 w-full text-sm font-medium text-white rounded-md border border-transparent shadow-sm bg-blue-primary hover:bg-blue-primary focus:outline-none focus:ring-2 focus:ring-blue-primary focus:ring-offset-2'
                                        >
                                            <p className='text-white'>Sign in</p>
                                        </button>
                                    ) : (
                                        <button
                                            disabled
                                            type='button'
                                            className='inline-flex justify-center items-center px-5 py-2.5 mr-2 w-full text-sm font-medium text-center text-white rounded-lg cursor-pointer bg-blue-primary hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 dark:bg-blue-primary dark:hover:bg-blue-primary dark:focus:ring-blue-800'
                                        >
                                            <svg
                                                role='status'
                                                className='inline mr-3 w-4 h-4 text-white animate-spin'
                                                viewBox='0 0 100 101'
                                                fill='none'
                                                xmlns='http://www.w3.org/2000/svg'
                                            >
                                                <path
                                                    d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
                                                    fill='#E5E7EB'
                                                />
                                                <path
                                                    d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
                                                    fill='currentColor'
                                                />
                                            </svg>
                                            Loading...
                                        </button>
                                    )}
                                </form>
                            </div>
                        </div>

                        {/* Replaced inline serverError box with top-right Notification */}
                        <Notification
                            showSavedNotification={showSavedNotification}
                            setShowSavedNotification={setShowSavedNotification}
                            title={notifTitle}
                            desc={notifDesc}
                            status={notifStatus}
                        />

                        {userAuthError && (
                            <div className='mt-6'>
                                <div className='p-4 bg-red-50 rounded-md'>
                                    <div className='flex'>
                                        <div className='flex-shrink-0'>
                                            <svg
                                                className='w-6 h-6 text-red-500'
                                                fill='none'
                                                stroke='currentColor'
                                                viewBox='0 0 24 24'
                                                xmlns='http://www.w3.org/2000/svg'
                                            >
                                                <path
                                                    strokeLinecap='round'
                                                    strokeLinejoin='round'
                                                    strokeWidth='2'
                                                    d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                                                ></path>
                                            </svg>
                                        </div>
                                        <div className='ml-3'>
                                            <h3 className='text-sm font-medium text-red-800'>Authentication Error</h3>
                                            <div className='mt-2 text-sm text-red-700'>
                                                <p>There was an error with your authentication. {userAuthError}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div>
                        <div>
                            <img className='w-auto h-20' src={logoSrc} alt='Your Company' />
                            <h2 className='my-6 text-3xl font-bold tracking-tight text-gray-900'>Jumpcloud Login</h2>
                        </div>
                        <button
                            onClick={jumpcloudLogin}
                            type='submit'
                            className='flex justify-center px-4 py-2 w-full text-sm font-medium text-white rounded-md border border-transparent shadow-sm bg-blue-primary hover:bg-blue-primary focus:outline-none focus:ring-2 focus:ring-blue-primary focus:ring-offset-2'
                        >
                            <p className='text-white'>Sign in with Jumpcloud</p>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
